import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import { getPosPaymentMethods, getTransactionsSummary, listTransactions } from '../lib/api';
import { walletWord } from '../lib/coin-label';
import { useCoinLabel } from '../lib/use-coin-label';
import { useActiveBranch } from '../lib/use-branch';
import { posStatusVariant } from '../lib/pos-status';
import { salesOrderSeries } from '../lib/sales-order';
import { todayStr, startOfLocalDay, endOfLocalDay } from '../lib/day';
import { BranchPicker } from '../components/branch-picker';
import { TransactionDetailDialog } from '../components/transaction-detail-dialog';

const STATUSES = ['', 'completed', 'refunded', 'cancelled', 'to_release'] as const;

function money(currency: string, value: string | number, coinLabel: string): string {
  const n = Number(value).toFixed(2);
  return currency === 'cash' ? `₱${n}` : `${n} ${walletWord(currency, coinLabel)}`;
}

/**
 * Transaction History (feature 12) — branch-scoped POS order history with
 * date / status / order-code / payment-method filters, defaulted to TODAY, and
 * a summary strip whose totals cover every matching row (the table itself is
 * capped at 500). Dates go to the server as LOCAL-day boundaries — a bare
 * `new Date('YYYY-MM-DD')` is UTC midnight and would hide pre-8am sales on a
 * UTC+8 tenant.
 */
export function TransactionsPage() {
  const { branchId, setSelectedBranchId, isScoped, branches } = useActiveBranch();
  const coinLabel = useCoinLabel();
  // The list is branch-scoped, so every row belongs to the active branch —
  // its prefix is what we strip to show the bare "Sales Order #" series.
  const orderPrefix = branches.find((b) => b.id === branchId)?.orderPrefix ?? null;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const [method, setMethod] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const filters = {
    branchId: branchId!,
    status: status || undefined,
    q: q || undefined,
    from: from ? startOfLocalDay(from).toISOString() : undefined,
    to: to ? endOfLocalDay(to).toISOString() : undefined,
    paymentMethod: method || undefined,
  };

  const query = useQuery({
    queryKey: ['pos-transactions', branchId, status, q, from, to, method],
    enabled: !!branchId,
    queryFn: () => listTransactions({ ...filters, limit: 500 }),
  });
  const summaryQ = useQuery({
    queryKey: ['pos-transactions-summary', branchId, status, q, from, to, method],
    enabled: !!branchId,
    queryFn: () => getTransactionsSummary(filters),
  });
  const methodsQ = useQuery({
    queryKey: ['pos-payment-methods'],
    queryFn: getPosPaymentMethods,
  });

  // Filter options: the enabled registry, unioned with every code seen in the
  // summary's per-method breakdown — so history rung up on a since-disabled
  // method stays filterable without any server support.
  const methodOptions = useMemo(() => {
    const opts = new Map<string, string>();
    for (const m of methodsQ.data ?? []) opts.set(m.code, m.label);
    for (const m of summaryQ.data?.byMethod ?? []) {
      if (m.code && !opts.has(m.code)) opts.set(m.code, m.label);
    }
    return [...opts.entries()].map(([code, label]) => ({ code, label }));
  }, [methodsQ.data, summaryQ.data]);

  const isToday = from === todayStr() && to === todayStr();
  const atDefaults = !status && !q && !method && isToday;
  const summary = summaryQ.data;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Sales Orders</CardTitle>
          <BranchPicker
            branches={branches}
            branchId={branchId}
            isScoped={isScoped}
            onChange={setSelectedBranchId}
          />
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Order / buyer</label>
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="0001 or name"
                className="w-44"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Status</label>
              <select
                className="h-10 rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s === '' ? 'All' : s}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Payment</label>
              <select
                className="h-10 rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
              >
                <option value="">All</option>
                {methodOptions.map((m) => (
                  <option key={m.code} value={m.code}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">From</label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">To</label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            {/* Clear = back to the DEFAULT view (today), not "all history" —
                an unbounded scan should be a deliberate act, not a reset. */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setStatus('');
                setQ('');
                setMethod('');
                setFrom(todayStr());
                setTo(todayStr());
              }}
              disabled={atDefaults}
            >
              Clear
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setFrom('');
                setTo('');
              }}
              disabled={!from && !to}
            >
              All time
            </Button>
          </div>

          {summary && summary.totals.length > 0 && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 rounded-md bg-muted/40 px-3 py-2 text-sm">
              {summary.totals.map((t) => (
                <span key={t.currency} className="tabular-nums">
                  <span className="font-medium">
                    {t.count} {t.count === 1 ? 'sale' : 'sales'} ·{' '}
                    {money(t.currency, t.total, coinLabel)}
                  </span>
                  {t.completedCount !== t.count && (
                    <span className="text-muted-foreground">
                      {' '}
                      ({money(t.currency, t.completedTotal, coinLabel)} completed)
                    </span>
                  )}
                </span>
              ))}
            </div>
          )}

          {query.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : !query.data?.length ? (
            <p className="text-sm text-muted-foreground">No sales orders match.</p>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-muted-foreground border-b">
                  <tr>
                    <th className="px-2 py-1.5">When</th>
                    <th className="px-2 py-1.5">Order</th>
                    <th className="px-2 py-1.5">Buyer</th>
                    <th className="px-2 py-1.5">Status</th>
                    <th className="px-2 py-1.5">Payment</th>
                    <th className="px-2 py-1.5 text-right">Items</th>
                    <th className="px-2 py-1.5 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.map((t) => (
                    <tr
                      key={t.orderId}
                      onClick={() => setSelected(t.orderId)}
                      className="cursor-pointer border-b last:border-0 hover:bg-muted/30"
                    >
                      <td className="px-2 py-1.5 text-xs text-muted-foreground">
                        {new Date(t.createdAt).toLocaleString()}
                      </td>
                      <td className="px-2 py-1.5 font-mono text-xs">
                        #{salesOrderSeries(t.code, orderPrefix)}
                        {/* Marks a sale that carries a remark, so a supervisor can
                          spot one to review without opening every row. */}
                        {t.hasNotes && (
                          <span className="ml-1 text-muted-foreground" title="Has remarks">
                            ✎
                          </span>
                        )}
                      </td>
                      {/* Server-resolved, so it already falls back to the buyer's
                        email — the `||` only covers a stale cached bundle. */}
                      <td className="px-2 py-1.5 text-xs">{t.buyerName || '—'}</td>
                      <td className="px-2 py-1.5">
                        <Badge variant={posStatusVariant(t.status)}>{t.status}</Badge>
                      </td>
                      <td className="px-2 py-1.5 text-xs">
                        {t.paymentMethodLabel ?? t.paymentMethod ?? '—'}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{t.itemCount}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {money(t.paymentCurrency, t.total, coinLabel)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {summary && summary.totals.length > 0 && (
                  <tfoot className="border-t text-xs">
                    <tr>
                      <td colSpan={6} className="px-2 py-1.5 text-right text-muted-foreground">
                        Total{summary.totals.length > 1 ? 's' : ''}
                      </td>
                      <td className="px-2 py-1.5 text-right font-medium tabular-nums">
                        {summary.totals
                          .map((t) => money(t.currency, t.total, coinLabel))
                          .join(' · ')}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
              {query.data.length === 500 && (
                <p className="text-xs text-muted-foreground">
                  Showing the first 500 rows — the totals above still cover the whole range.
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
      <TransactionDetailDialog
        orderId={selected}
        orderPrefix={orderPrefix}
        onClose={() => setSelected(null)}
      />
    </>
  );
}
